// Sound utility for generating audio feedback using Web Audio API
export class SoundGenerator {
  private audioContext: AudioContext | null = null;
  private initialized = false;
  private volume: number = 0.5;

  constructor() {
    // Bind all methods to ensure 'this' context is preserved
    this.timerEnd = this.timerEnd.bind(this);
    this.setComplete = this.setComplete.bind(this);
    this.exerciseComplete = this.exerciseComplete.bind(this);
    this.restEnd = this.restEnd.bind(this);
    this.workoutComplete = this.workoutComplete.bind(this);
    this.playTone = this.playTone.bind(this);
  }

  private initAudioContext() {
    if (this.initialized) return true;

    try {
      // Create audio context (will be activated on first user interaction)
      this.audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      this.initialized = true;
      return true;
    } catch (error) {
      console.error("Web Audio API is not supported in this browser", error);
      return false;
    }
  }

  // Add getter for audio context
  getAudioContext(): AudioContext | null {
    return this.audioContext;
  }

  // Add setter for volume
  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
  }

  /**
   * Play a beep sound with specified parameters
   * @param frequency - Frequency in Hz
   * @param duration - Duration in milliseconds
   * @param type - Oscillator type (sine, square, sawtooth, triangle)
   * @param volumeOverride - Optional volume override
   */
  playTone(
    frequency: number = 440,
    duration: number = 200,
    type: OscillatorType = "sine",
    volumeOverride?: number
  ) {
    // Use the provided volume or fall back to the instance volume
    const volume = volumeOverride !== undefined ? volumeOverride : this.volume;

    // Initialize on demand
    if (!this.audioContext) {
      const initialized = this.initAudioContext();
      if (!initialized) return; // Can't initialize audio
    }

    try {
      // Create oscillator and gain nodes
      const oscillator = this.audioContext!.createOscillator();
      const gainNode = this.audioContext!.createGain();

      // Configure oscillator
      oscillator.type = type;
      oscillator.frequency.value = frequency;

      // Configure volume with a slight fade in/out to avoid clicks
      const now = this.audioContext!.currentTime;
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(volume, now + 0.01);
      gainNode.gain.linearRampToValueAtTime(0, now + duration / 1000);

      // Connect nodes
      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext!.destination);

      // Start and stop the tone
      oscillator.start(now);
      oscillator.stop(now + duration / 1000 + 0.05); // Add a small buffer
    } catch (error) {
      console.error("Error playing tone:", error);
    }
  }

  // Predefined sound patterns

  // Simple beep for timer end
  timerEnd() {
    try {
      this.playTone(880, 200, "sine", 0.3);
    } catch (e) {
      console.error("Error playing timer end sound:", e);
    }
  }

  // Double beep for set completion
  setComplete() {
    try {
      this.playTone(660, 100, "sine", 0.3);
      setTimeout(() => this.playTone(880, 100, "sine", 0.3), 150);
    } catch (e) {
      console.error("Error playing set complete sound:", e);
    }
  }

  // Triple ascending beep for exercise completion
  exerciseComplete() {
    try {
      this.playTone(440, 100, "sine", 0.3);
      setTimeout(() => this.playTone(554, 100, "sine", 0.3), 150);
      setTimeout(() => this.playTone(659, 200, "sine", 0.3), 300);
    } catch (e) {
      console.error("Error playing exercise complete sound:", e);
    }
  }

  // Rest period end alert - make it more distinctive
  restEnd() {
    try {
      // Use a more attention-grabbing sound pattern with higher volume
      // First a low beep
      this.playTone(330, 150, "square", 0.5);

      // Then two higher beeps
      setTimeout(() => this.playTone(660, 150, "square", 0.5), 200);
      setTimeout(() => this.playTone(660, 150, "square", 0.5), 400);

      // Final higher beep with higher volume
      setTimeout(() => this.playTone(880, 250, "square", 0.6), 600);
    } catch (e) {
      console.error("Error playing rest end sound:", e);
    }
  }

  // Victory fanfare for workout completion
  workoutComplete() {
    try {
      this.playTone(523, 100, "triangle", 0.4); // C
      setTimeout(() => this.playTone(659, 100, "triangle", 0.4), 100); // E
      setTimeout(() => this.playTone(784, 100, "triangle", 0.4), 200); // G
      setTimeout(() => this.playTone(1047, 300, "triangle", 0.4), 350); // High C
    } catch (e) {
      console.error("Error playing workout complete sound:", e);
    }
  }

  // Get audio context state
  getAudioContextState() {
    if (!this.audioContext) {
      this.initAudioContext();
    }
    return this.audioContext?.state || "suspended";
  }

  // Resume audio context
  resumeAudioContext() {
    if (this.audioContext && this.audioContext.state === "suspended") {
      this.audioContext
        .resume()
        .then(() => console.log("AudioContext resumed successfully"))
        .catch((err) => console.error("Failed to resume AudioContext:", err));
    }
  }
}

// Create and export a singleton instance
export const soundPlayer = new SoundGenerator();
